import Reveal from "@/components/Reveal";

const CAPABILITIES = [
  {
    num: "01",
    label: "photonic matrix-vector multiplication",
    body: "Every inference is computed as a physical matrix-vector multiply — optical signals through weighted modulators, summed by photodetectors.",
  },
  {
    num: "02",
    label: "multi-wavelength compute",
    body: "WDM parallelism assigns independent wavelength channels to separate compute paths, multiplying effective throughput without scaling array size.",
  },
  {
    num: "03",
    label: "photonic inference inside full AI model graphs",
    body: "Run full pretrained MLPs and CNNs through the photonic pipeline — seven models from MNIST Linear to CIFAR-10 ResNet-8 are included.",
  },
];

const FEATURES = [
  {
    label: "— Physics Reference",
    title: "A full photonic compute textbook. Baked in.",
    body: "21 sections of rendered physics — LaTeX transfer functions, intuition callouts, and wins/losses tables for every modulator and noise source. Every equation the simulator uses is documented and explained.",
  },
  {
    label: "— Live Comparison",
    title: "Ideal and photonic inference run side by side.",
    body: "Every run shows IDEAL FLOAT32 and PHOTONIC WITH NOISE simultaneously — including full confusion matrices for both. The gap between them is the hardware cost, made visible.",
  },
  {
    label: "— Hardware Sizing",
    title: "Eight live hardware sliders. One bottleneck chart.",
    body: "Tune array width, modulation rate, ADC/DAC sample rates, weight precision, and batch size — throughput and bottleneck recompute in real time. The per-tile time budget stacked bar shows exactly where your cycles go.",
  },
  {
    label: "— Design Space Sweep",
    title: "Sweep any parameter. Plot the Pareto frontier.",
    body: "Pick a non-ideality parameter, enter comma-separated values, add an optional second axis for a 2D heatmap, run — the simulator plots accuracy vs throughput across every configuration. Hardware co-design at simulation speed.",
  },
  {
    label: "— Dual Physics Paths",
    title: "Incoherent or coherent. Two completely different physics.",
    body: "Switch to coherent detection and the system uses homodyne readout — signed weights emerge naturally from the interference term. A full mode change with its own physics engine.",
  },
  {
    label: "— Download",
    title: "Run a sweep. Get publication-ready figures.",
    body: "After any Pareto sweep, hit Download — the simulator generates a ZIP of clean, formatted plots ready for a paper or slide deck. The figures arrive ready to use.",
  },
];

export default function WhatItDoes() {
  return (
    <section id="what-it-does" className="relative pb-28 md:pb-40" data-testid="what-it-does-section">
      <div className="container-x">
        <Reveal>
          <div className="clip-frame-sm relative overflow-hidden border border-white/10 bg-panel p-8 md:p-14">
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "radial-gradient(45% 60% at 90% 0%, rgba(0,229,255,0.10), transparent 70%)",
              }}
            />
            <div className="relative z-10">
              <p className="eyebrow mb-8 text-beam">What the simulator does</p>
              <p className="max-w-4xl text-2xl font-light leading-relaxed text-zinc-200 md:text-3xl">
                The QPI Labs simulator runs real neural-network models on a photonic-compute
                abstraction. It lets developers explore photonic matrix-vector multiplication,
                multi-wavelength compute, and photonic inference behavior inside full AI model
                graphs — long before hardware exists.
              </p>
              <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-3">
                {CAPABILITIES.map((c, i) => (
                  <Reveal key={c.num} delay={0.06 * i}>
                    <div
                      className="flex h-full flex-col border border-white/10 bg-black/30 p-5"
                      data-testid={`capability-${c.num}`}
                    >
                      <div className="flex items-baseline gap-4">
                        <span className="font-mono text-[11px] font-bold text-laser">{c.num}</span>
                        <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-300">
                          {c.label}
                        </span>
                      </div>
                      <p className="mt-3 font-mono text-[11px] leading-relaxed text-zinc-500">{c.body}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
              <p className="eyebrow mt-14 mb-6 text-laser">— What&apos;s inside the simulator</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {FEATURES.map((f, i) => (
                  <Reveal key={f.title} delay={0.04 * i}>
                    <div
                      className="h-full rounded-lg border border-white/10 bg-black/30 p-6"
                      style={{ boxShadow: "inset 0 0 24px rgba(61,107,255,0.07)" }}
                      data-testid={`inside-card-${i + 1}`}
                    >
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{f.label}</p>
                      <p className="mt-3 font-mono text-sm font-bold leading-relaxed text-zinc-100">{f.title}</p>
                      <p className="mt-3 font-mono text-[11px] leading-relaxed text-zinc-500">{f.body}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
              <p className="mt-8 text-center font-mono text-[11px] text-zinc-600" data-testid="numpy-footnote">
                Everything runs on real NumPy compute — real physics, real numbers.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
